// Growem — behavioral rules (canonical). Streak, progressive overload, protein debt, set-keys.

import type { DayState, Exercise } from '../types'
import { GOALS, planForDate } from '../data/plan'
import { dow, diffDays, addDays } from './dates'

/** Minimum machine step: 5 kg for prensa / leg press / pantorrilla, else 2.5 kg. */
export function stepFor(name: string): number {
  return /prensa|leg\s*press|pantorrilla/i.test(name) ? 5 : 2.5
}

/** First number found in a string like "127 kg × 8" → 127. */
export function firstNumber(s: string | undefined): number | null {
  if (!s) return null
  const m = s.replace(',', '.').match(/-?\d+(\.\d+)?/)
  return m ? Number(m[0]) : null
}

/**
 * Progressive-overload target: never the last weight. If we can read a numeric last mark,
 * bump it by the machine step; otherwise fall back to the plan's prescribed target string.
 */
export function nextTarget(ex: Exercise, lastWeight?: number | null): string {
  const base = lastWeight ?? firstNumber(ex.last)
  if (base == null) return ex.t || ex.last
  const next = base + stepFor(ex.n)
  const reps = ex.t.match(/×\s*([\d–-]+)/)?.[1] ?? '8–10'
  return `${trimNum(next)} kg × ${reps}`
}

function trimNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n)
}

/**
 * The prescribed rep range [lo, hi] for an exercise, parsed from its target string then its meta
 * line. Accepts BOTH the V1 en-dash rep ranges (U+2013) and the V2 plain-hyphen ranges (e.g. "8-10").
 * A single rep count ("× 12") yields [12, 12]; nothing parseable defaults to [8, 10].
 */
export function repRange(ex: Exercise): [number, number] {
  const RANGE = /[×x]\s*(\d+)\s*[–-]\s*(\d+)/
  const SINGLE = /[×x]\s*(\d+)/
  for (const s of [ex.t, ex.meta]) {
    const m = s?.match(RANGE)
    if (m) return [Number(m[1]), Number(m[2])]
  }
  for (const s of [ex.t, ex.meta]) {
    const m = s?.match(SINGLE)
    if (m) { const n = Number(m[1]); return [n, n] }
  }
  return [8, 10]
}

/**
 * Tier-1 double-progression target from the user's REAL last top set. Always strictly greater than
 * last: below the top of the rep range, add ONE rep at the same load; at/above the top, add the
 * machine step and drop to the bottom of the range. This is the textbook double-progression overload
 * scheme (progress reps to the top of a range, then add load) — defensible for hypertrophy because
 * reps taken close to failure across a wide range produce comparable growth, so building reps before
 * load guarantees progressive overload without losing stimulus. Refs: Legion / MesoStrength / W2.
 */
export function nextTargetFromHistory(ex: Exercise, lastW: number, lastR: number, assistance = false): { w: number; reps: number; str: string } {
  const [lo, hi] = repRange(ex)
  const step = stepFor(ex.n)
  let w = lastW
  let reps: number
  if (lastR >= hi) {
    // Earned progression. For a NORMAL load: add the step. For an ASSISTANCE load (assisted pull-up:
    // more kg = easier), progression is INVERTED: subtract the step (floor 0 = unassisted).
    w = assistance ? Math.max(0, lastW - step) : lastW + step
    reps = lo
  } else { reps = lastR + 1 }                      // add one rep at the same load
  const unit = assistance ? 'asistencia ' : ''
  return { w, reps, str: `${unit}${trimNum(w)} kg × ${reps}` }
}

/** A day counts toward the streak iff at least one logged SET is marked done.
 *  Warm-up / cool-down checks use `prep-*` / `post-*` keys and must NOT count. */
export function dayCounts(d: DayState | undefined): boolean {
  return !!d && Object.keys(d.done).some((k) => /^\d/.test(k))
}

/**
 * Current streak ending at `todayIso`, scanning back up to 365 days.
 * trained day → +1; planned rest (restDow) → +1; today untrained → skip;
 * ONE non-rest miss is forgiven (grace) so a single slip doesn't zero the habit; the SECOND
 * non-rest miss breaks. Habits survive an occasional missed day without resetting the underlying
 * behaviour (Lally et al. 2010: "missing one opportunity to perform the behaviour did not
 * materially affect the habit formation process"). The forgiven day does NOT add +1 (you didn't
 * train): it only lets the scan bridge to earlier trained days. Sunday-rest handling is unchanged.
 */
export function currentStreak(days: Record<string, DayState>, todayIso: string, restDow = GOALS.restDow): number {
  let streak = 0
  let grace = 1 // single forgiveness token for one non-rest miss
  for (let i = 0; i < 365; i++) {
    const iso = addDays(todayIso, -i)
    if (dayCounts(days[iso])) {
      streak++
    } else if (dow(iso) === restDow) {
      streak++
    } else if (i === 0) {
      // today not yet trained: neither adds nor breaks
    } else if (grace > 0) {
      grace-- // bridge one gap: streak neither grows nor resets
    } else {
      break
    }
  }
  return streak
}

/** Best historical streak across all recorded days (never regresses vs a stored value). */
export function bestStreak(days: Record<string, DayState>, todayIso: string, restDow = GOALS.restDow): number {
  const keys = Object.keys(days).sort()
  if (keys.length === 0) return 0
  let run = 0, best = 0
  const from = keys[0]
  const span = Math.max(0, diffDays(todayIso, from))
  for (let i = 0; i <= span; i++) {
    const iso = addDays(from, i)
    if (dayCounts(days[iso])) run++
    else if (dow(iso) === restDow) run++
    else run = 0
    if (run > best) best = run
  }
  return best
}

/** Protein debt after 6pm: max(0, floor - prot) when hour ≥ debtHour, else 0. */
export function proteinDebt(prot: number, hour: number, floor = GOALS.proteinMin, debtHour = GOALS.proteinDebtHour): number {
  if (hour < debtHour) return 0
  return Math.max(0, floor - prot)
}

// ---------- set-key parsing ----------
export interface ParsedKey { d: number; e: number; s: number; c: number } // c = -1 for a normal set

export function parseSetKey(key: string): ParsedKey {
  const p = key.split('-').map(Number)
  if (p.length === 4) return { d: p[0], e: p[1], s: p[2], c: p[3] }
  return { d: p[0], e: p[1], s: p[2], c: -1 }
}

/** Set-key builders. */
export function setKey(d: number, e: number, s: number): string { return `${d}-${e}-${s}` }
export function pairKey(d: number, e: number, round: number, child: number): string { return `${d}-${e}-${round}-${child}` }
export function weightKey(d: number, e: number): string { return `w${d}-${e}` }

/** Session progress: done vs total "rondas" (a superset round counts once, when both children done). */
export function sessionProgress(day: DayState | undefined, planIndex: number | null): { doneN: number; totN: number } {
  if (planIndex == null || !day) return { doneN: 0, totN: 0 }
  const plan = planForDate(day.day)[planIndex]
  if (!plan) return { doneN: 0, totN: 0 }
  let doneN = 0, totN = 0
  plan.ex.forEach((ex, e) => {
    if (ex.pair) {
      for (let r = 0; r < ex.sets; r++) {
        totN++
        if (day.done[pairKey(planIndex, e, r, 0)] && day.done[pairKey(planIndex, e, r, 1)]) doneN++
      }
    } else {
      for (let s = 0; s < ex.sets; s++) {
        totN++
        if (day.done[setKey(planIndex, e, s)]) doneN++
      }
    }
  })
  return { doneN, totN }
}

/**
 * How many NORMAL (non-superset) set rows to render for exercise `e`: the plan's `planSets`, or more
 * if extra sets were added (their keys `d-e-s` persist in done/log; add appends the next index and
 * remove pops the last, so indices stay contiguous 0..count-1). Guards an undefined day and ignores
 * prep-/post-/weight keys via the exact (c===-1, d, e) filter.
 *
 * IMPORTANT: this feeds RENDERING and the "all rendered sets done" auto-advance gate ONLY. It does NOT
 * change sessionProgress or exComplete, so an added set is a BONUS that never blocks the plan's
 * completion, the % bar, the exercise checkmark, or the "train" task (all stay plan-based).
 */
export function normalSetsCount(day: DayState | undefined, planIndex: number, e: number, planSets: number): number {
  if (!day) return planSets
  let maxS = planSets - 1
  for (const store of [day.done, day.log]) {
    for (const k of Object.keys(store)) {
      if (!/^\d/.test(k)) continue // skip prep-*/post-* (and w* weight keys never start with a digit)
      const pk = parseSetKey(k)
      if (pk.c === -1 && pk.d === planIndex && pk.e === e && Number.isInteger(pk.s)) maxS = Math.max(maxS, pk.s)
    }
  }
  return maxS + 1
}

/**
 * Whether EVERY rendered normal set (plan + any extras) is done. Used to gate auto-advance so adding a
 * bonus set holds the advance until you actually do it, WITHOUT affecting completion (see normalSetsCount).
 */
export function allNormalSetsDone(day: DayState, planIndex: number, e: number, planSets: number): boolean {
  const count = normalSetsCount(day, planIndex, e, planSets)
  for (let s = 0; s < count; s++) if (!day.done[setKey(planIndex, e, s)]) return false
  return true
}

/**
 * Keys of the BELOW sets (index > fromS) of the same normal exercise that a WEIGHT cascade should fill:
 * not done, and currently EMPTY of weight. Non-destructive by construction (never overwrites a typed
 * weight or a done set); weight only, because reps legitimately drop across sets with fatigue.
 */
export function cascadeWeightKeys(day: DayState, planIndex: number, e: number, fromS: number, planSets: number): string[] {
  const count = normalSetsCount(day, planIndex, e, planSets)
  const out: string[] = []
  for (let s = fromS + 1; s < count; s++) {
    const k = setKey(planIndex, e, s)
    if (day.done[k]) continue        // never touch a completed set
    if (day.log[k]?.w != null) continue // never overwrite a typed weight (empty-only)
    out.push(k)
  }
  return out
}

/**
 * Reorder ONE exercise's sets for a day: move the PAYLOAD (log incl. rest/at, done flag, note) from
 * index `from` to index `to`, shifting the rest. Pure permutation over indices 0..count-1, so the key
 * set (and with it the extra-set count) is preserved; empty positions travel as deletions. Analytics
 * are order-independent (top-set selection), so this is data-safe by construction.
 */
export function reorderSetData(day: DayState, planIndex: number, e: number, from: number, to: number, planSets: number): DayState {
  const count = normalSetsCount(day, planIndex, e, planSets)
  if (from === to || from < 0 || to < 0 || from >= count || to >= count) return day
  const keyAt = (s: number) => setKey(planIndex, e, s)
  const payloads = Array.from({ length: count }, (_, s) => ({
    log: day.log[keyAt(s)], done: day.done[keyAt(s)], note: day.notes[keyAt(s)],
  }))
  const [moved] = payloads.splice(from, 1)
  payloads.splice(to, 0, moved)
  const log = { ...day.log }, done = { ...day.done }, notes = { ...day.notes }
  payloads.forEach((pl, s) => {
    const k = keyAt(s)
    if (pl.log !== undefined) log[k] = pl.log; else delete log[k]
    if (pl.done !== undefined) done[k] = pl.done; else delete done[k]
    if (pl.note !== undefined) notes[k] = pl.note; else delete notes[k]
  })
  return { ...day, log, done, notes }
}

/** Whether a plan exercise's block is fully complete for the day. */
export function exComplete(day: DayState, planIndex: number, e: number, ex: Exercise): boolean {
  if (ex.pair) {
    for (let r = 0; r < ex.sets; r++) {
      if (!(day.done[pairKey(planIndex, e, r, 0)] && day.done[pairKey(planIndex, e, r, 1)])) return false
    }
    return true
  }
  for (let s = 0; s < ex.sets; s++) if (!day.done[setKey(planIndex, e, s)]) return false
  return true
}
